import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, friendlyError } from "../lib/api";
import { ImpactBadge, RelevanceBar, SourceLevelTag, APPL_COLOR, Pill } from "../lib/ui";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, CheckCircle2, Building2, Package, Users, Sparkles, GitCommit,
  ClipboardCheck, History,
} from "lucide-react";

function Section({ title, children, icon: Icon }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2">
        {Icon && <Icon size={16} className="text-blue-600" />}{title}
      </h3>
      {children}
    </div>
  );
}

export default function RadarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/regulatory/${id}`).then((r) => setD(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const createChange = async () => {
    setBusy(true);
    try {
      await api.post(`/regulatory/${id}/create-change`);
      toast.success("Change record e attività creati. Vai su Action Hub.");
      navigate("/app/tasks");
    } catch (e) { toast.error(friendlyError(e)); } finally { setBusy(false); }
  };

  const review = async (decision) => {
    try {
      await api.post("/reviews", { object_id: id, object_type: "regulatory_document", decision });
      toast.success(`Review: ${decision}`);
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  if (notFound) return <div className="text-slate-500">Documento non trovato o non disponibile. <Link to="/app/radar" className="text-blue-600 hover:underline">Torna al Radar</Link></div>;
  if (!d) return <div className="text-slate-400">Caricamento...</div>;
  const a = d.applicability_analysis;

  return (
    <div className="ri-fade-in max-w-5xl">
      <Link to="/app/radar" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={15} /> Radar Regolatorio
      </Link>

      <div className="flex items-start gap-3 flex-wrap mb-2">
        <span className="font-mono text-xs text-slate-600 font-medium">{d.authority}</span>
        <SourceLevelTag level={d.source_level} />
        <Pill>{d.doc_type}</Pill>
        <Pill>{d.status}</Pill>
        {d.is_demo && <Pill color="#6B21A8" bg="#F3E8FF">DEMO</Pill>}
      </div>
      <h1 className="font-head text-2xl font-bold text-slate-900 leading-tight">{d.title_official}</h1>
      {d.title_translated && d.title_translated !== d.title_official && (
        <p className="text-sm text-slate-500 mt-1">Titolo tradotto: {d.title_translated}</p>
      )}
      <a href={d.url} target="_blank" rel="noreferrer" data-testid="reg-source-link"
        className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:underline">
        <ExternalLink size={14} /> Fonte ufficiale
      </a>

      {/* Relevance vs Impact */}
      <div className="grid md:grid-cols-2 gap-4 my-5">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="ri-overline mb-2">Rilevanza (sito)</div>
          <div className="flex items-center gap-3">
            <div className="font-head text-4xl font-bold text-slate-900">{a.relevance_score}%</div>
            <span className="font-medium" style={{ color: APPL_COLOR[a.applicability] }}>{a.applicability}</span>
          </div>
          <RelevanceBar score={a.relevance_score} />
          <ul className="mt-3 space-y-1 text-xs text-slate-600">
            {a.reasons.map((r, i) => <li key={i} className="flex gap-2"><span className="text-blue-500">•</span>{r}</li>)}
          </ul>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="ri-overline mb-2">Impatto</div>
          <ImpactBadge level={a.impact} />
          <ul className="mt-3 space-y-1 text-xs text-slate-600">
            {(a.impact_reasons || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-orange-500">•</span>{r}</li>)}
          </ul>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Executive summary"><p className="text-sm text-slate-600 leading-relaxed">{d.ai_summary}</p></Section>
        <Section title="Cosa è cambiato"><p className="text-sm text-slate-600 leading-relaxed">{d.whats_new}</p></Section>
        <Section title="Perché è importante per la nostra CDMO">
          <p className="text-sm text-slate-600 leading-relaxed">{d.why_important}</p>
          <p className="text-sm text-slate-600 leading-relaxed mt-2">{d.cdmo_impact}</p>
        </Section>
        <Section title="Metadati regolatori">
          <dl className="text-xs text-slate-600 grid grid-cols-2 gap-y-1.5">
            <dt className="text-slate-400">Pubblicazione</dt><dd>{d.publication_date || "—"}</dd>
            <dt className="text-slate-400">Effective date</dt><dd>{d.effective_date || "—"}</dd>
            <dt className="text-slate-400">Implementation</dt><dd>{d.implementation_date || "—"}</dd>
            <dt className="text-slate-400">Versione</dt><dd>{d.version || "—"}</dd>
            <dt className="text-slate-400">Lingua originale</dt><dd>{d.language_original}</dd>
            <dt className="text-slate-400">Superseded</dt><dd>{d.superseded_doc || "—"}</dd>
            <dt className="text-slate-400">Last verified</dt><dd>{(d.last_verified || "").slice(0, 10)}</dd>
            <dt className="text-slate-400">Review status</dt><dd>{d.review_status}</dd>
          </dl>
        </Section>
      </div>

      {/* Affected clients / products */}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <Section title="Clienti potenzialmente interessati" icon={Users}>
          <div className="space-y-1.5">
            {d.affected_clients.map((c) => (
              <div key={c.id} className="text-sm flex items-center justify-between">
                <span>{c.name}</span>
                <span className="text-xs text-slate-400 font-mono">{(c.markets || []).join(", ")}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Prodotti potenzialmente interessati" icon={Package}>
          <div className="space-y-1.5">
            {d.affected_products.map((p) => (
              <div key={p.id} className="text-sm flex items-center justify-between">
                <span>{p.name}</span>
                <span className="text-xs text-slate-400 font-mono">{(p.markets || []).join(", ")}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Actions */}
      <div className="rounded-xl bg-slate-900 text-white p-5 mt-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="ri-overline" style={{ color: "#64748B" }}>Azioni</div>
          <div className="text-sm text-slate-300 mt-1">Trasforma questo segnale in un workflow tracciabile.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={createChange} disabled={busy} data-testid="create-change-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-60">
            <GitCommit size={16} /> Crea Change + Attività
          </button>
          <button onClick={() => review("Accettato")} data-testid="review-accept-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm transition-colors">
            <CheckCircle2 size={16} /> Accetta review
          </button>
          <button onClick={() => review("Ulteriore approfondimento")}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm transition-colors">
            <ClipboardCheck size={16} /> Approfondisci
          </button>
        </div>
      </div>

      {d.reviews?.length > 0 && (
        <Section title="Review history" icon={History}>
          <div className="space-y-2">
            {d.reviews.map((r) => (
              <div key={r.id} className="text-xs text-slate-600 flex gap-2">
                <span className="font-medium">{r.reviewer}</span>
                <span className="text-slate-400">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-blue-600">{r.decision}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      <p className="mt-5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
        Valutazione preliminare AI. Le valutazioni devono essere verificate da personale qualificato prima
        dell'uso per decisioni regolatorie, GMP o qualità.
      </p>
    </div>
  );
}
